import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, PanelLeftClose, PanelLeftOpen, X, Plane, BedDouble, Utensils, Ticket, Play } from 'lucide-react';
import './App.css';
import { Event } from './types';
import { initialEvents } from './dataStore';
import { buildDemoData } from './demoData';
import CalendarView from './components/CalendarView';
import ListView from './components/ListView';
import PlanningView from './components/PlanningView';
import EventForm, { EVENT_CATEGORY_OPTIONS, type Category } from './components/EventForm';

type PrimaryMode = 'calendar' | 'list' | 'planning';

const CATEGORY_ICONS: Partial<Record<Category, React.ComponentType<{ size?: number; color?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>>> = {
  travel: Plane,
  stay: BedDouble,
  reservation: Utensils,
  activity: Ticket,
};

const App: React.FC = () => {
  const [events, setEvents] = useState<Event[]>(initialEvents);
  const [mode, setMode] = useState<PrimaryMode>('calendar');
  const [trips, setTrips] = useState<string[]>([]);
  const [tripName, setTripName] = useState<string | null>(null);
  const [newTripName, setNewTripName] = useState('');
  const [showEventForm, setShowEventForm] = useState(false);
  const [eventDropdownAnchor, setEventDropdownAnchor] = useState<{ left: number; top: number; right?: number; date?: Date } | null>(null);
  const [eventFormInitialCategory, setEventFormInitialCategory] = useState<Category | undefined>(undefined);
  const [eventFormInitialDate, setEventFormInitialDate] = useState<Date | undefined>(undefined);
  const [showAddTripModal, setShowAddTripModal] = useState(false);
  const [isTripHover, setIsTripHover] = useState<string | null>(null);
  const [isNewTripHover, setIsNewTripHover] = useState(false);
  const [isStartPlanningHover, setIsStartPlanningHover] = useState(false);
  const [isAddTripHover, setIsAddTripHover] = useState(false);
  const [isSidebarToggleHover, setIsSidebarToggleHover] = useState(false);
  const [isAddEventHover, setIsAddEventHover] = useState(false);
  const [isAddEventSubmitHover, setIsAddEventSubmitHover] = useState(false);
  const [isCancelEventHover, setIsCancelEventHover] = useState(false);
  const [isCloseEventHover, setIsCloseEventHover] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState<Category | null>(null);
  const [isEventFormValid, setIsEventFormValid] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [isDemoButtonHover, setIsDemoButtonHover] = useState(false);
  const [isExitDemoHover, setIsExitDemoHover] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('sidebarCollapsed') === 'true';
    } catch {
      return false;
    }
  });
  const [planningContent, setPlanningContent] = useState<Record<string, string>>({});
  const eventDropdownRef = useRef<HTMLDivElement>(null);
  const addEventButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!eventDropdownAnchor) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (eventDropdownRef.current && !eventDropdownRef.current.contains(e.target as Node)) {
        setEventDropdownAnchor(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [eventDropdownAnchor]);

  useEffect(() => {
    if (mode === 'planning') setEventDropdownAnchor(null);
  }, [mode]);

  const handleCreateEvent = (eventData: Omit<Event, 'id'>) => {
    const newEvent: Event = {
      ...eventData,
      id: crypto.randomUUID ? crypto.randomUUID() : String(Date.now()),
      endDate: eventData.endDate,
      trip: tripName ?? undefined,
    };
    setEvents((prev) => [...prev, newEvent]);
    closeEventForm();
  };

  const closeEventForm = () => {
    setShowEventForm(false);
    setEventFormInitialCategory(undefined);
    setEventFormInitialDate(undefined);
    setIsEventFormValid(false);
  };

  const openEventForm = (initialCategory?: Category) => {
    setEventFormInitialDate(eventDropdownAnchor?.date);
    setEventFormInitialCategory(initialCategory);
    setShowEventForm(true);
    setEventDropdownAnchor(null);
  };

  const openAddEventDropdownFromCalendar = useCallback((date: Date, anchorRect: DOMRect) => {
    setEventDropdownAnchor({
      left: anchorRect.left,
      top: anchorRect.bottom + 4,
      date,
    });
  }, []);

  const canCreateTrip = newTripName.trim().length > 0;

  /** Light pill button: dimmed and inert until enabled, white with a ring on hover. */
  const primaryButtonStyle = (enabled: boolean, isHover: boolean): React.CSSProperties => {
    const active = enabled && isHover;
    return {
      borderRadius: '9999px',
      border: 'none',
      background: !enabled ? '#3a3a3f' : active ? '#ffffff' : '#e5e7eb',
      color: !enabled ? '#8b8b93' : '#050509',
      boxShadow: active ? '0 0 0 3px rgba(255, 255, 255, 0.3)' : 'none',
      transform: active ? 'translateY(-1px)' : 'none',
      transition: 'background 120ms, box-shadow 120ms, transform 120ms, color 120ms',
      fontWeight: 600,
      fontSize: '0.95rem',
      cursor: enabled ? 'pointer' : 'not-allowed',
    };
  };

  const startDemo = () => {
    const demo = buildDemoData();
    setTrips(demo.trips);
    setEvents(demo.events);
    setPlanningContent(demo.planningContent);
    setTripName(demo.trips[0]);
    setMode('calendar');
    setNewTripName('');
    setIsDemo(true);
  };

  const exitDemo = () => {
    closeEventForm();
    setShowAddTripModal(false);
    setEventDropdownAnchor(null);
    setTrips([]);
    setEvents(initialEvents);
    setPlanningContent({});
    setTripName(null);
    setIsDemo(false);
  };

  const tripEvents = events.filter((e) => e.trip === tripName);

  const handleCreateTrip = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!canCreateTrip) return;
    const name = newTripName.trim();
    setTrips((prev) => [name, ...prev]);
    setTripName(name);
    setMode('calendar');
    setShowAddTripModal(false);
    setNewTripName('');
  };

  useEffect(() => {
    try {
      localStorage.setItem('sidebarCollapsed', String(isSidebarCollapsed));
    } catch {
      // Storage unavailable; collapse state just won't persist.
    }
  }, [isSidebarCollapsed]);

  const hasTrips = trips.length > 0;

  const railButtonStyle = (isHover: boolean): React.CSSProperties => ({
    width: '32px',
    height: '32px',
    borderRadius: '6px',
    border: 'none',
    background: isHover ? '#303030' : 'transparent',
    color: isHover ? '#e5e7eb' : '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    flexShrink: 0,
    transition: 'background 120ms, color 120ms',
  });

  React.useEffect(() => {
    if (!showAddTripModal) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowAddTripModal(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [showAddTripModal]);

  return (
    <div
      style={{
        height: '100vh',
        minHeight: 0,
        overflow: 'hidden',
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: !hasTrips ? '1fr' : isSidebarCollapsed ? '56px 1fr' : '260px 1fr',
          transition: 'grid-template-columns 160ms',
          gridTemplateRows: '1fr',
          gap: 0,
          alignItems: 'stretch',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Left panel: fixed height, no scroll — only calendar scrolls. Hidden until a trip exists. */}
        {hasTrips && (
          <aside
            style={{
              minHeight: 0,
              overflow: 'hidden',
              background: '#202020',
              borderRight: '1px solid #27272f',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {isSidebarCollapsed ? (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  padding: '1rem 0',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '6px',
                  overflowY: 'auto',
                }}
              >
                <button
                  type="button"
                  aria-label="Expand sidebar"
                  title="Expand sidebar"
                  onClick={() => setIsSidebarCollapsed(false)}
                  onMouseEnter={() => setIsSidebarToggleHover(true)}
                  onMouseLeave={() => setIsSidebarToggleHover(false)}
                  style={railButtonStyle(isSidebarToggleHover)}
                >
                  <PanelLeftOpen size={18} aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label="New trip"
                  title="New trip"
                  onClick={() => setShowAddTripModal(true)}
                  onMouseEnter={() => setIsNewTripHover(true)}
                  onMouseLeave={() => setIsNewTripHover(false)}
                  style={railButtonStyle(isNewTripHover)}
                >
                  <Plus size={18} aria-hidden="true" />
                </button>
                <div style={{ width: '24px', borderTop: '1px solid #27272f', margin: '4px 0' }} />
                {trips.map((trip) => {
                  const isActive = tripName === trip;
                  const isHover = isTripHover === trip;
                  return (
                    <button
                      key={trip}
                      type="button"
                      aria-label={trip}
                      title={trip}
                      onClick={() => setTripName(trip)}
                      onMouseEnter={() => setIsTripHover(trip)}
                      onMouseLeave={() => setIsTripHover(null)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '9999px',
                        border: 'none',
                        flexShrink: 0,
                        background: isActive ? '#e5e7eb' : isHover ? '#3f3f46' : '#303030',
                        color: isActive ? '#050509' : '#e5e7eb',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'background 120ms',
                      }}
                    >
                      {trip.charAt(0).toUpperCase()}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  padding: '1rem 0',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "1rem",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.9rem",
                      fontWeight: 600,
                      color: "#e5e7eb",
                      paddingLeft: "4px",
                    }}
                  >
                    Travel Planner
                  </div>
                  <button
                    type="button"
                    aria-label="Collapse sidebar"
                    title="Collapse sidebar"
                    onClick={() => setIsSidebarCollapsed(true)}
                    onMouseEnter={() => setIsSidebarToggleHover(true)}
                    onMouseLeave={() => setIsSidebarToggleHover(false)}
                    style={{ ...railButtonStyle(isSidebarToggleHover), width: "28px", height: "28px" }}
                  >
                    <PanelLeftClose size={16} aria-hidden="true" />
                  </button>
                </div>

                <div style={{ paddingLeft: "8px", paddingRight: "8px", display: "flex", flexDirection: "column", gap: "2px" }}>
                  <button
                    type="button"
                    onClick={() => setShowAddTripModal(true)}
                    onMouseEnter={() => setIsNewTripHover(true)}
                    onMouseLeave={() => setIsNewTripHover(false)}
                    style={{
                      padding: "4px",
                      borderRadius: "4px",
                      border: "none",
                      background: isNewTripHover ? "#303030" : "transparent",
                      color: isNewTripHover ? "#e5e7eb" : "#9ca3af",
                      fontSize: "0.9rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      cursor: "pointer",
                      textAlign: "left",
                      transition: "background 120ms, color 120ms",
                    }}
                  >
                    <Plus size={14} aria-hidden="true" />
                    New trip
                  </button>
                  <div style={{ borderTop: "1px solid #27272f", margin: "6px 0" }} />
                  {trips.map((trip) => (
                    <div
                      key={trip}
                      role="button"
                      tabIndex={0}
                      onClick={() => setTripName(trip)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setTripName(trip);
                        }
                      }}
                      style={{
                        padding: "4px",
                        borderRadius: "4px",
                        background: tripName === trip ? "#303030" : isTripHover === trip ? "#303030" : "transparent",
                        border: "none",
                        fontWeight: tripName === trip ? 700 : 400,
                        color: "#e5e7eb",
                        fontSize: "0.9rem",
                        cursor: "pointer",
                      }}
                      onMouseEnter={() => setIsTripHover(trip)}
                      onMouseLeave={() => setIsTripHover(null)}
                    >
                      {trip}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        )}

        {showAddTripModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(0,0,0,0.55)',
              zIndex: 50,
            }}
            onClick={() => setShowAddTripModal(false)}
          >
            <div
              style={{
                background: '#202020',
                borderRadius: '0.75rem',
                padding: '1.5rem',
                width: '100%',
                maxWidth: 400,
                border: '1px solid #27272f',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '1rem',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#e5e7eb' }}>
                  New trip
                </h3>
                <button
                  type="button"
                  onClick={() => setShowAddTripModal(false)}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#9ca3af',
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              </div>
              <form
                onSubmit={handleCreateTrip}
                style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
              >
                <div>
                  <label
                    htmlFor="modal-trip-name"
                    style={{
                      display: 'block',
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      color: '#9ca3af',
                      marginBottom: '0.35rem',
                    }}
                  >
                    Trip name
                  </label>
                  <input
                    id="modal-trip-name"
                    type="text"
                    placeholder="e.g. Chamonix ski week"
                    value={newTripName}
                    onChange={(e) => setNewTripName(e.target.value)}
                    autoFocus
                    style={{
                      width: '100%',
                      padding: '0.6rem 0.75rem',
                      borderRadius: '0.5rem',
                      border: '1px solid #27272f',
                      background: '#191919',
                      color: '#e5e7eb',
                      fontSize: '0.95rem',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={!canCreateTrip}
                  onMouseEnter={() => setIsAddTripHover(true)}
                  onMouseLeave={() => setIsAddTripHover(false)}
                  style={{
                    ...primaryButtonStyle(canCreateTrip, isAddTripHover),
                    padding: '0.6rem 1.25rem',
                    alignSelf: 'flex-end',
                  }}
                >
                  Add new trip
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Main content: scroll stays inside calendar/list/planning area */}
        <main
          style={{
            paddingTop: '1rem',
            paddingLeft: '1rem',
            paddingRight: '1rem',
            paddingBottom: '24px',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {!tripName ? (
            <section
              id="create-trip-cta"
              style={{
                maxWidth: 520,
                margin: "3rem auto 0",
                background: "#050509",
                borderRadius: "1rem",
                border: "1px solid #27272f",
                padding: "2rem",
                textAlign: "left",
              }}
            >
              <p style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600 }}>Welcome to your travel planner</p>
              <h1 style={{ margin: "0.5rem 0 0", fontSize: "1.6rem" }}>Start planning a new trip</h1>
              <p
                style={{
                  margin: "0.5rem 0 1.5rem",
                  color: "#9ca3af",
                  fontSize: "0.95rem",
                }}
              >
                Create your first trip to see it in the left panel and start adding events
                to the calendar.
              </p>
              <form
                onSubmit={handleCreateTrip}
                style={{ display: "flex", gap: "0.75rem" }}
              >
                <input
                  type="text"
                  placeholder="e.g. Chamonix ski week"
                  value={newTripName}
                  onChange={(e) => setNewTripName(e.target.value)}
                  style={{
                    flex: 1,
                    padding: "0.75rem 0.9rem",
                    borderRadius: "9999px",
                    border: "1px solid #27272f",
                    background: "#101117",
                    color: "#e5e7eb",
                    fontSize: "0.95rem",
                  }}
                />
                <button
                  type="submit"
                  disabled={!canCreateTrip}
                  onMouseEnter={() => setIsStartPlanningHover(true)}
                  onMouseLeave={() => setIsStartPlanningHover(false)}
                  style={{
                    ...primaryButtonStyle(canCreateTrip, isStartPlanningHover),
                    padding: "0.75rem 1.4rem",
                  }}
                >
                  Start planning
                </button>
              </form>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  margin: "1.25rem 0",
                  color: "#6b7280",
                  fontSize: "0.8rem",
                }}
              >
                <div style={{ flex: 1, height: 1, background: "#2a2a2e" }} />
                or
                <div style={{ flex: 1, height: 1, background: "#2a2a2e" }} />
              </div>
              <button
                type="button"
                onClick={startDemo}
                onMouseEnter={() => setIsDemoButtonHover(true)}
                onMouseLeave={() => setIsDemoButtonHover(false)}
                style={{
                  width: "100%",
                  padding: "0.75rem 1.4rem",
                  borderRadius: "9999px",
                  border: `1px solid ${isDemoButtonHover ? "#3f3f46" : "#27272f"}`,
                  background: isDemoButtonHover ? "#202020" : "transparent",
                  color: "#e5e7eb",
                  fontWeight: 600,
                  fontSize: "0.95rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  cursor: "pointer",
                  transition: "background 120ms, border-color 120ms",
                }}
              >
                <Play size={16} aria-hidden="true" />
                Explore demo mode
              </button>
              <p style={{ margin: "0.6rem 0 0", textAlign: "center", color: "#9ca3af", fontSize: "0.8rem" }}>
                Look around with sample trips — nothing you do is saved.
              </p>
            </section>
          ) : (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
              }}
            >
              {isDemo && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.6rem",
                    padding: "0.4rem 0.5rem 0.4rem 0.75rem",
                    marginBottom: "0.75rem",
                    borderRadius: "0.5rem",
                    border: "1px solid #27272f",
                    background: "#202020",
                    fontSize: "0.85rem",
                    color: "#9ca3af",
                    flexShrink: 0,
                  }}
                >
                  <span
                    style={{
                      padding: "0.1rem 0.45rem",
                      borderRadius: "0.25rem",
                      background: "#8F7036",
                      color: "#f9fafb",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Demo
                  </span>
                  <span style={{ flex: 1 }}>You're exploring sample trips. Changes aren't saved.</span>
                  <button
                    type="button"
                    onClick={exitDemo}
                    onMouseEnter={() => setIsExitDemoHover(true)}
                    onMouseLeave={() => setIsExitDemoHover(false)}
                    style={{
                      padding: "0.35rem 0.8rem",
                      borderRadius: "9999px",
                      border: "none",
                      background: isExitDemoHover ? "#303030" : "transparent",
                      color: isExitDemoHover ? "#f9fafb" : "#e5e7eb",
                      fontSize: "0.8rem",
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "background 120ms, color 120ms",
                    }}
                  >
                    Exit demo
                  </button>
                </div>
              )}
              <div
                style={{
                  marginBottom: "0.75rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "1rem",
                  flexShrink: 0,
                }}
              >
                <div>
                  <h2 style={{ margin: 0, fontSize: "1.2rem" }}>{tripName}</h2>
                  <div
                    style={{
                      marginTop: "0.5rem",
                      display: "inline-flex",
                      padding: "0.2rem",
                      borderRadius: "9999px",
                      background: "#050509",
                      border: "1px solid #27272f",
                      gap: "0.15rem",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setMode('calendar')}
                      style={{
                        padding: "0.25rem 0.75rem",
                        borderRadius: "9999px",
                        border: "none",
                        background: mode === 'calendar' ? "#e5e7eb" : "transparent",
                        color: mode === 'calendar' ? "#050509" : "#9ca3af",
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      Calendar
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode('list')}
                      style={{
                        padding: "0.25rem 0.75rem",
                        borderRadius: "9999px",
                        border: "none",
                        background: mode === 'list' ? "#e5e7eb" : "transparent",
                        color: mode === 'list' ? "#050509" : "#9ca3af",
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      List
                    </button>
                    <button
                      type="button"
                      onClick={() => setMode('planning')}
                      style={{
                        padding: "0.25rem 0.75rem",
                        borderRadius: "9999px",
                        border: "none",
                        background: mode === 'planning' ? "#e5e7eb" : "transparent",
                        color: mode === 'planning' ? "#050509" : "#9ca3af",
                        fontSize: "0.8rem",
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                    >
                      Planning
                    </button>
                  </div>
                </div>
                {mode !== 'planning' && (
                <div
                  ref={addEventButtonRef}
                  style={{ position: 'relative' }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (eventDropdownAnchor) {
                        setEventDropdownAnchor(null);
                      } else if (addEventButtonRef.current) {
                        const rect = addEventButtonRef.current.getBoundingClientRect();
                        setEventDropdownAnchor({ left: rect.left, top: rect.bottom + 4, right: rect.right });
                      }
                    }}
                    onMouseEnter={() => setIsAddEventHover(true)}
                    onMouseLeave={() => setIsAddEventHover(false)}
                    style={{
                      padding: "0.7rem 1.4rem",
                      borderRadius: "9999px",
                      border: "none",
                      background: isAddEventHover || eventDropdownAnchor ? "#1f6fc4" : "#2883DF",
                      color: "#f9fafb",
                      fontWeight: 500,
                      fontSize: "0.875rem",
                      cursor: "pointer",
                      boxShadow: isAddEventHover
                        ? "0 12px 28px rgba(40, 131, 223, 0.35)"
                        : "0 10px 25px rgba(15, 23, 42, 0.25)",
                      transform: isAddEventHover ? "translateY(-1px)" : "none",
                      transition: "background 120ms, box-shadow 120ms, transform 120ms",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.35rem",
                    }}
                  >
                    Add Event
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0.9,
                      }}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M6 9l6 6 6-6"
                          stroke="#ffffff"
                          strokeWidth="2.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                  {eventDropdownAnchor && (
                    <div
                      ref={eventDropdownRef}
                      style={{
                        position: 'fixed',
                        ...(eventDropdownAnchor.right != null
                          ? { right: window.innerWidth - eventDropdownAnchor.right }
                          : { left: eventDropdownAnchor.left }),
                        top: eventDropdownAnchor.top,
                        minWidth: 200,
                        background: '#202020',
                        border: '1px solid #27272f',
                        borderRadius: '0.5rem',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.4)',
                        padding: '4px 0',
                        zIndex: 50,
                      }}
                    >
                      {EVENT_CATEGORY_OPTIONS.map(({ value, label }) => {
                        const Icon = CATEGORY_ICONS[value];
                        const isHover = hoveredCategory === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => openEventForm(value)}
                            onMouseEnter={() => setHoveredCategory(value)}
                            onMouseLeave={() => setHoveredCategory(null)}
                            style={{
                              width: 'calc(100% - 8px)',
                              margin: '0 4px',
                              padding: '0.55rem 0.75rem',
                              border: 'none',
                              borderRadius: '0.375rem',
                              background: isHover ? '#2a2a2e' : 'transparent',
                              color: '#e5e7eb',
                              fontSize: '0.9rem',
                              textAlign: 'left',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            {Icon && <Icon size={16} color={isHover ? '#e5e7eb' : '#9ca3af'} aria-hidden="true" />}
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                )}
              </div>

          {tripName && showEventForm && (
            <div
              role="presentation"
              onClick={closeEventForm}
              style={{
                position: 'fixed',
                inset: 0,
                display: 'flex',
                justifyContent: 'flex-end',
                background: 'rgba(0,0,0,0.55)',
                zIndex: 40,
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '420px',
                  maxWidth: '100%',
                  height: '100%',
                  background: '#202020',
                  borderLeft: '1px solid #27272f',
                  boxShadow: '-12px 0 30px rgba(0,0,0,0.6)',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '1.25rem 1rem 0.75rem',
                    flexShrink: 0,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: '#9ca3af',
                      }}
                    >
                      New event
                    </div>
                    <h3
                      style={{
                        margin: '0.25rem 0 0',
                        fontSize: '1.1rem',
                        color: '#e5e7eb',
                      }}
                    >
                      {eventFormInitialCategory
                        ? `${eventFormInitialCategory.charAt(0).toUpperCase()}${eventFormInitialCategory.slice(1)}`
                        : 'Details'}
                    </h3>
                  </div>
                  <button
                    type="button"
                    aria-label="Close"
                    title="Close"
                    onClick={closeEventForm}
                    onMouseEnter={() => setIsCloseEventHover(true)}
                    onMouseLeave={() => setIsCloseEventHover(false)}
                    style={{ ...railButtonStyle(isCloseEventHover), marginRight: '-6px' }}
                  >
                    <X size={18} aria-hidden="true" />
                  </button>
                </div>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', padding: '0 1rem', overflow: 'hidden' }}>
                  <EventForm
                    formId="event-drawer-form"
                    onCreate={handleCreateEvent}
                    initialCategory={eventFormInitialCategory}
                    initialDate={eventFormInitialDate}
                    onValidityChange={setIsEventFormValid}
                  />
                </div>
                <div
                  style={{
                    flexShrink: 0,
                    width: '100%',
                    padding: '1rem',
                    background: '#191919',
                    borderTop: '1px solid #27272f',
                    boxSizing: 'border-box',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                  }}
                >
                  <button
                    type="button"
                    onClick={closeEventForm}
                    onMouseEnter={() => setIsCancelEventHover(true)}
                    onMouseLeave={() => setIsCancelEventHover(false)}
                    style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '9999px',
                      border: 'none',
                      background: isCancelEventHover ? '#2a2a2e' : 'transparent',
                      color: isCancelEventHover ? '#e5e7eb' : '#9ca3af',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      transition: 'background 120ms, color 120ms',
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    form="event-drawer-form"
                    type="submit"
                    disabled={!isEventFormValid}
                    onMouseEnter={() => setIsAddEventSubmitHover(true)}
                    onMouseLeave={() => setIsAddEventSubmitHover(false)}
                    style={{
                      ...primaryButtonStyle(isEventFormValid, isAddEventSubmitHover),
                      padding: '0.5rem 1.25rem',
                      fontSize: '0.875rem',
                    }}
                  >
                    Add Event
                  </button>
                </div>
              </div>
            </div>
          )}

              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                {tripName && mode === 'calendar' && (
                  <CalendarView
                    key={tripName}
                    events={tripEvents}
                    onAddEventInDay={openAddEventDropdownFromCalendar}
                  />
                )}
                {tripName && mode === 'list' && <ListView events={tripEvents} />}
                {tripName && mode === 'planning' && (
                  <PlanningView
                    key={tripName}
                    value={planningContent[tripName] ?? ''}
                    onChange={(value) =>
                      setPlanningContent((prev) => ({ ...prev, [tripName]: value }))
                    }
                  />
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default App;
